"use client";
import { useEffect, useState } from "react";
import { useGameStore } from "@/stores/game-store";
import { useAuthStore } from "@/stores/auth-store";

export function useGameData() {
  const setDepartments = useGameStore((s) => s.setDepartments);
  const setSections = useGameStore((s) => s.setSections);
  const setSurggies = useGameStore((s) => s.setSurggies);
  const setUser = useAuthStore((s) => s.setUser);
  const [loaded, setLoaded] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const [meRes, deptRes, surggiesRes] = await Promise.all([
          fetch("/api/auth/me", { credentials: "include" }),
          fetch("/api/departments"),
          fetch("/api/surggies"),
        ]);

        console.log("[useGameData] /api/auth/me status:", meRes.status);
        const meData = await meRes.json();
        console.log("[useGameData] /api/auth/me data:", JSON.stringify(meData));

        if (meRes.ok) {
          setUser(meData.user);
        } else {
          setAuthFailed(true);
        }

        if (deptRes.ok) {
          const data = await deptRes.json();
          setDepartments(
            data.departments?.map((d: Record<string, unknown>) => ({
              id: d.id,
              name: d.name,
              type: d.type,
              gridX: d.gridX ?? d.grid_x ?? 0,
              gridY: d.gridY ?? d.grid_y ?? 0,
              level: d.level ?? 1,
            })) ?? []
          );
          const allSections = data.departments?.flatMap(
            (d: Record<string, unknown>) =>
              ((d.sections as Record<string, unknown>[]) ?? []).map((s) => ({
                id: s.id,
                departmentId: d.id,
                name: s.name,
                type: s.type,
                gridX: s.gridX ?? s.grid_x ?? 0,
                gridY: s.gridY ?? s.grid_y ?? 0,
              }))
          ) ?? [];
          setSections(allSections);
        }

        if (surggiesRes.ok) {
          const data = await surggiesRes.json();
          const TILE_SIZE = 16;
          const BUILDING_PLOTS = [
            { col: 5, row: 8, width: 10, height: 8 },
            { col: 20, row: 8, width: 10, height: 8 },
            { col: 35, row: 8, width: 10, height: 8 },
            { col: 50, row: 8, width: 10, height: 8 },
            { col: 65, row: 8, width: 10, height: 8 },
            { col: 5, row: 22, width: 10, height: 8 },
            { col: 20, row: 22, width: 10, height: 8 },
            { col: 35, row: 22, width: 10, height: 8 },
            { col: 50, row: 22, width: 10, height: 8 },
            { col: 65, row: 22, width: 10, height: 8 },
          ];
          setSurggies(
            data.surggies?.map((s: Record<string, unknown>, i: number) => {
              const plot = BUILDING_PLOTS[i % BUILDING_PLOTS.length];
              const doorCol = plot.col + Math.floor(plot.width / 2);
              const doorRow = plot.row + plot.height - 1;
              return {
                id: s.id as string,
                name: s.name as string,
                role: s.role as string,
                team: s.team as string,
                spriteKey: (s.sprite_key ?? s.spriteKey ?? "surggie_1") as string,
                llmModel: (s.llm_model ?? s.llmModel ?? "claude-4.6") as string,
                isPrimary: (s.is_primary ?? s.isPrimary ?? false) as boolean,
                position: {
                  x: doorCol * TILE_SIZE,
                  y: (doorRow - 2) * TILE_SIZE,
                },
                status: (s.status ?? "idle") as "idle" | "working" | "resting",
                level: (s.level ?? 1) as number,
                xp: (s.xp ?? 0) as number,
              };
            }) ?? []
          );
        }
      } catch (err) {
        console.error("Failed to load game data:", err);
        setAuthFailed(true);
      } finally {
        setLoaded(true);
      }
    }

    loadData();
  }, [setDepartments, setSections, setSurggies, setUser]);

  return { loaded, authFailed };
}
